import { FormikHelpers } from 'formik'
import * as React from 'react'

import { Form, Forms, Name, Username } from '@ally/forms'
import { Box, Button, Space } from '@ally/metronome-ui'
import { AsyncStatus } from '@ally/transmitigator'

import { useHostServices } from '../../../HostServices'
import { useTransmit } from '../../../TransmitContext'
import { FeatureFlag } from '../../../Utils'
import { CIAMErrorBanner, InputWrapper, StyledTextBody } from '../../Shared'

interface PersonalForgotPasswordFormProps {
  error?: string | undefined
  isAutoBusinessUser: boolean
  isResetPassword?: boolean
  status: AsyncStatus | 'Blocked' | 'Error'
  toggle: () => void
  username?: string
}
interface PersonalFormProps {
  username: string
  lastName: string
}

const FORM_ID = 'personal-forgot-password-form'

export const PersonalForgotPasswordForm: React.FC<PersonalForgotPasswordFormProps> = ({
  error,
  isAutoBusinessUser,
  isResetPassword,
  status,
  toggle,
  username = '',
}) => {
  const { actions, navigateToForgotUsername } = useTransmit()
  const { featureFlags } = useHostServices()
  const isAutoNGAEnabled = featureFlags.variation(FeatureFlag.AutoNGA, true)
  const initialValues: PersonalFormProps = {
    username,
    lastName: '',
  }

  const onSubmit = (
    form: PersonalFormProps,
    { setSubmitting }: FormikHelpers<PersonalFormProps>,
  ): void => {
    actions.recoverPassword({
      username: form.username,
      lastName: form.lastName,
      isResetPassword,
      journey_version: 'v2',
      isAutoBusinessUser,
    })
    // NOTE: this allows for form resubmission after failure
    setSubmitting(false)
  }

  const handleDontKnowUsernameClick = (): void => {
    navigateToForgotUsername()
  }

  return (
    <>
      <Space mb="md">
        <StyledTextBody size="sm" tag="p">
          To recover your password or create a new one, tell us your last name
          and your username.
        </StyledTextBody>
      </Space>

      {isAutoNGAEnabled && (
        <Space mb="md">
          <Button
            onClick={toggle}
            text="Have an auto business account?"
            type="button"
            variant="link"
          />
        </Space>
      )}

      <CIAMErrorBanner mb="md" error={error} />

      <Forms formId={FORM_ID} initialValues={initialValues} onSubmit={onSubmit}>
        <Form autoComplete="off" id={FORM_ID}>
          <InputWrapper mb="md">
            <Name
              variant="Last Name"
              name="lastName"
              data-allytmfn="lastName"
              required
              data-testid="enroll-last-name"
            />
          </InputWrapper>

          <InputWrapper mb="sm">
            <Username name="username" useDefaultValidate />
          </InputWrapper>

          <Box mb="md">
            <Button
              allytmln="forgotPassword-dontKnowUsername"
              onClick={handleDontKnowUsernameClick}
              variant="link"
              text="Don't know your username?"
              data-testid="forgot-username-button-link"
            />
          </Box>

          <Button
            allytmln="forgotPassword-enterUsername-continue"
            type="submit"
            variant="primary"
            text="Continue"
            loading={status === 'Loading'}
          />
        </Form>
      </Forms>
    </>
  )
}
